import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Alert, Spin, Space, Typography, Input, Tag, message } from 'antd';
import AiAgreementIndicator from './AiAgreementIndicator';
import { useApiRequest } from '../hooks/useApiRequest';
import { formatDdMmYyyy } from '../constants/dashboardTiles';
import { maturityBandLabel } from '../utils/maturity';
import MaturityDomainBreakdown from './MaturityDomainBreakdown';

const { Text, Paragraph } = Typography;

/**
 * Lead Advisor review of an AI dashboard tile (Commandment IV; ISO/IEC 42001 A.9.2 human
 * oversight). The approver generates a DRAFT, reads exactly what the model produced, and approves
 * THAT draft: the approve call carries the `snapshotId` the approver was shown, so the server
 * approves that row and nothing newer. A draft that was superseded or is older than 24h comes back
 * 409 DASHBOARD_SNAPSHOT_NOT_PENDING and the approver must review again.
 *
 * Contract: policylens-backend/src/controllers/dashboardTileApproval.controller.ts
 *   GET  .../ai-tiles/:tileId/approval  -> { buildable, needsMoreDataReason, approval, active, pendingDraft }
 *   POST .../preview                     -> 201 { draft: { snapshotId, computedAt, value, confidence } }
 *   POST .../approve { snapshotId }      -> { approval, snapshot }
 *   POST .../revoke  { reason? }         -> { approval, active: false }
 * Approving is enforced by the API (Lead Advisor of the project, real caller, never an impersonated
 * one; platform_admin gets 403 PLATFORM_ADMIN_APPROVAL_DENIED, PRD s2.2). `canApprove` is false for
 * every other role, so the approve control is not rendered; preview / read / revoke stay available.
 */

function DraftBody({ value }) {
  if (value && typeof value.narrative === 'string') {
    return (
      <div data-testid="approval-draft-body">
        <Text strong>The model says:</Text>
        <Paragraph style={{ marginBottom: 0 }}>{value.narrative}</Paragraph>
      </div>
    );
  }
  if (value && typeof value.maturityScore === 'number') {
    return (
      <div data-testid="approval-draft-body">
        <Paragraph style={{ marginBottom: 4 }}>
          <Text strong>
            Maturity score: {value.maturityScore} / 100{value.maturityBand ? ` (${maturityBandLabel(value.maturityBand)})` : ''}
          </Text>
        </Paragraph>
        {value.justification && (
          <>
            <Text strong>The model says:</Text>
            <Paragraph style={{ marginBottom: 8 }}>{value.justification}</Paragraph>
          </>
        )}
        <MaturityDomainBreakdown domains={Array.isArray(value.domains) ? value.domains : null} testId="approval-maturity-domains" defaultOpen />
      </div>
    );
  }
  return (
    <Text type="secondary" data-testid="approval-draft-body">
      {JSON.stringify(value)}
    </Text>
  );
}

// Mirrors the backend's STANDING_APPROVAL_MAX_AGE_DAYS (services/dashboard/dashboardRead.ts).
const STANDING_APPROVAL_MAX_AGE_DAYS = 30;

const STALE_COPY = 'This draft changed or expired - review it again.';

const DashboardTileApprovalModal = ({ open, onClose, projectId, tile, onChanged, canApprove = false }) => {
  const { request } = useApiRequest();
  const tileId = tile?.tileId;
  const base = `/projects/${projectId}/dashboard/ai-tiles/${tileId}`;
  const [loadState, setLoadState] = useState('loading'); // 'loading' | 'error' | 'ready'
  const [info, setInfo] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null); // 'preview' | 'approve' | 'revoke' | null
  const [notice, setNotice] = useState(null); // { type, text }
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');

  const loadInfo = useCallback(async () => {
    setLoadState('loading');
    try {
      const { data } = await request('GET', `${base}/approval`);
      setInfo(data);
      setDraft(data?.pendingDraft || null);
      setLoadState('ready');
    } catch (err) {
      setNotice({ type: 'error', text: err.message || 'Could not load the approval status.' });
      setLoadState('error');
    }
  }, [request, base]);

  useEffect(() => {
    if (open && tileId) loadInfo();
    // Keyed on the tile being reviewed; `loadInfo` changes identity with `base`, which is derived from it.
  }, [open, tileId]);

  const fail = (err, fallback) => setNotice({ type: 'error', text: err?.message || fallback });

  const generate = async () => {
    setBusy('preview');
    setNotice(null);
    try {
      const { data } = await request('POST', `${base}/preview`);
      setDraft(data.draft);
    } catch (err) {
      if (err.status === 409) setNotice({ type: 'warning', text: err.message });
      else if (err.status === 503) setNotice({ type: 'error', text: 'The AI draft could not be generated. Try again shortly.' });
      else fail(err, 'Could not generate a preview.');
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    if (!draft?.snapshotId) return;
    setBusy('approve');
    setNotice(null);
    try {
      await request('POST', `${base}/approve`, { body: { snapshotId: draft.snapshotId } });
      message.success('Approved. This summary is now visible on the dashboard.');
      if (onChanged) onChanged();
      onClose();
    } catch (err) {
      if (err.status === 409 && err.code === 'DASHBOARD_SNAPSHOT_NOT_PENDING') {
        setDraft(null);
        await loadInfo();
        // loadInfo re-seeds the draft from the server's newest pending one; the approver has NOT seen
        // that one, so it is cleared and they must explicitly generate/review again.
        setDraft(null);
        setNotice({ type: 'warning', text: STALE_COPY });
      } else if (err.status === 403) {
        setNotice({
          type: 'error',
          text: `${err.message} Approvals must be made by the Lead Advisor themselves, not while acting on someone else's behalf.`,
        });
      } else {
        fail(err, 'Approval failed.');
      }
    } finally {
      setBusy(null);
    }
  };

  const revoke = async () => {
    setBusy('revoke');
    setNotice(null);
    try {
      await request('POST', `${base}/revoke`, { body: reason.trim() ? { reason: reason.trim() } : {} });
      message.success('Approval revoked. The summary is hidden until it is approved again.');
      if (onChanged) onChanged();
      onClose();
    } catch (err) {
      if (err.status === 409) {
        setRevoking(false);
        await loadInfo();
        setNotice({ type: 'warning', text: 'There is no active approval to revoke.' });
      } else {
        fail(err, 'Revoke failed.');
      }
    } finally {
      setBusy(null);
    }
  };

  const active = !!info?.active;
  const needsMoreData = info && info.buildable === false;

  return (
    <Modal open={open} onCancel={onClose} title={`Review: ${tile?.title || tileId}`} footer={null} width={640}>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {notice && <Alert type={notice.type} showIcon message={notice.text} data-testid="approval-notice" />}

        {loadState === 'loading' && <Spin />}
        {loadState === 'error' && (
          <Button onClick={loadInfo} data-testid="approval-retry">
            Retry
          </Button>
        )}

        {loadState === 'ready' && (
          <>
            {active && (
              <Alert
                type="info"
                showIcon
                data-testid="approval-active"
                message={`Approved on ${formatDdMmYyyy(info.approval?.approvedAt)}. The summary refreshes automatically under this approval.`}
                description={`Auto-refreshed values stop being shown ${STANDING_APPROVAL_MAX_AGE_DAYS} days after approval. Generate and approve a new draft before then to renew it.`}
              />
            )}

            {needsMoreData && (
              <Alert
                type="warning"
                showIcon
                data-testid="approval-needs-data"
                message="Needs more data"
                description={
                  info.needsMoreDataReason ||
                  'There is not enough recorded project data to write this summary yet, so there is nothing to approve.'
                }
              />
            )}

            {!needsMoreData && !draft && (
              <Button type="primary" loading={busy === 'preview'} onClick={generate} data-testid="approval-generate">
                {active ? 'Generate a new draft to review' : 'Generate draft to review'}
              </Button>
            )}

            {draft && (
              <div data-testid="approval-draft" style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 12 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <Space>
                    <Tag color="purple">AI drafted - review</Tag>
                    <Text type="secondary">Generated {formatDdMmYyyy(draft.computedAt)}</Text>
                  </Space>
                  <DraftBody value={draft.value} />
                  <AiAgreementIndicator
                    confidence={draft.confidence}
                    runs={draft.confidenceRuns}
                    validSamples={draft.validSamples}
                    reasoningRefs={draft.reasoningRefs}
                    unavailableReason={draft.confidenceUnavailableReason}
                  />
                  {!canApprove && (
                    <Text type="secondary" data-testid="approval-lead-advisor-only">
                      Only the Lead Advisor of this project can approve a draft.
                    </Text>
                  )}
                  <Space>
                    {canApprove && (
                      <Button type="primary" loading={busy === 'approve'} onClick={approve} data-testid="approval-approve">
                        Approve this exact draft
                      </Button>
                    )}
                    <Button loading={busy === 'preview'} onClick={generate} data-testid="approval-regenerate">
                      Discard and generate again
                    </Button>
                  </Space>
                </Space>
              </div>
            )}

            {active && !revoking && (
              <Button danger onClick={() => setRevoking(true)} data-testid="approval-revoke-open">
                Revoke approval
              </Button>
            )}
            {active && revoking && (
              <Space direction="vertical" style={{ width: '100%' }} size={8} data-testid="approval-revoke-form">
                <Text>Revoking hides this summary from everyone until a new draft is approved.</Text>
                <Input
                  placeholder="Reason (optional)"
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="approval-revoke-reason"
                />
                <Space>
                  <Button danger type="primary" loading={busy === 'revoke'} onClick={revoke} data-testid="approval-revoke-confirm">
                    Confirm revoke
                  </Button>
                  <Button onClick={() => setRevoking(false)}>Cancel</Button>
                </Space>
              </Space>
            )}
          </>
        )}
      </Space>
    </Modal>
  );
};

export default DashboardTileApprovalModal;
